import React from "react";
import { Card, Button, Avatar, Spin, Empty } from "antd";
import {
  ReadOutlined,
  TeamOutlined,
  FileDoneOutlined,
  RiseOutlined,
  UnorderedListOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ClassChips } from "./ClassChips";
import { scoreColor, initials } from "./evalDashboardUtils";
import styles from "./TeacherEvalDashboard.module.scss";

function Kpi({ icon, value, label }) {
  return (
    <Card className={styles.kpiCard} variant="outlined">
      <span className={styles.kpiIcon}>{icon}</span>
      <span className={styles.kpiText}>
        <span className={styles.kpiValue}>{value}</span>
        <span className={styles.kpiLabel}>{label}</span>
      </span>
    </Card>
  );
}

// T1 — Dashboard del profesor: selector de clase + KPIs + apartados.
export function DashboardHome({
  classNumbers,
  selectedClass,
  onSelectClass,
  summary,
  loadingSummary,
  quizzes,
  onGoExams,
  onGoStudents,
}) {
  const { t } = useTranslation("evaluations");
  const students = summary?.students || [];
  const examsTotal = summary?.examsTotal ?? quizzes?.length ?? 0;
  const classAverage = summary?.classAverage ?? 0;
  const topStudents = [...students]
    .sort((a, b) => (b.avgScore || 0) - (a.avgScore || 0))
    .slice(0, 3);
  const previewExams = (quizzes || []).slice(0, 2);

  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t("dashboard.title")}</h2>
        <p className={styles.subtitle}>{t("dashboard.subtitle")}</p>
      </div>

      <ClassChips
        classes={classNumbers}
        selected={selectedClass}
        onSelect={onSelectClass}
      />

      {loadingSummary ? (
        <div className={styles.center}>
          <Spin />
        </div>
      ) : (
        <>
          <div className={styles.kpiRow}>
            <Kpi
              icon={<ReadOutlined />}
              value={classNumbers?.length || 0}
              label={t("dashboard.kpiClasses")}
            />
            <Kpi
              icon={<TeamOutlined />}
              value={students.length}
              label={
                selectedClass
                  ? t("dashboard.kpiStudentsOf", { class: selectedClass })
                  : t("dashboard.kpiStudents")
              }
            />
            <Kpi
              icon={<FileDoneOutlined />}
              value={examsTotal}
              label={t("dashboard.kpiExams")}
            />
            <Kpi
              icon={<RiseOutlined />}
              value={`${classAverage} %`}
              label={t("dashboard.kpiAverage")}
            />
          </div>

          <div className={styles.sectionRow}>
            {/* Apartado Examenes */}
            <Card className={styles.sectionCard} variant="outlined">
              <div className={styles.sectionHead}>
                <span className={styles.kpiIcon}>
                  <UnorderedListOutlined />
                </span>
                <div>
                  <h3 className={styles.sectionTitle}>
                    {t("dashboard.sectionExams")}
                  </h3>
                  <span className={styles.sectionMeta}>
                    {t("dashboard.examsCreated", { count: examsTotal })}
                  </span>
                </div>
              </div>

              <div className={styles.miniList}>
                {previewExams.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t("dashboard.noExams")}
                  />
                ) : (
                  previewExams.map((q) => (
                    <div className={styles.miniItem} key={q._id}>
                      <span className={styles.miniName}>
                        <span>{q.name || "—"}</span>
                      </span>
                      <span className={styles.sectionMeta}>
                        {t("dashboard.questionsCount", {
                          count: Array.isArray(q.questions)
                            ? q.questions.length
                            : 0,
                        })}
                      </span>
                    </div>
                  ))
                )}
              </div>

              <Button
                className={styles.btnGreen}
                size="large"
                block
                onClick={onGoExams}
              >
                {t("dashboard.manageExams")}
              </Button>
            </Card>

            {/* Apartado Alumnos */}
            <Card className={styles.sectionCard} variant="outlined">
              <div className={styles.sectionHead}>
                <span className={styles.kpiIcon}>
                  <TeamOutlined />
                </span>
                <div>
                  <h3 className={styles.sectionTitle}>
                    {selectedClass
                      ? t("dashboard.kpiStudentsOf", { class: selectedClass })
                      : t("dashboard.sectionStudents")}
                  </h3>
                  <span className={styles.sectionMeta}>
                    {t("dashboard.studentsCount", { count: students.length })}
                  </span>
                </div>
              </div>

              <div className={styles.miniList}>
                {topStudents.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t("dashboard.noStudents")}
                  />
                ) : (
                  topStudents.map((s) => (
                    <div className={styles.miniItem} key={s.studentId}>
                      <span className={styles.miniName}>
                        <Avatar size={26} src={s.avatar || undefined}>
                          {initials(s.name, s.surname)}
                        </Avatar>
                        <span>
                          {s.name} {s.surname}
                        </span>
                      </span>
                      <span
                        className={styles.scorePill}
                        style={{ background: scoreColor(s.avgScore) }}
                      >
                        {s.avgScore} %
                      </span>
                    </div>
                  ))
                )}
              </div>

              <Button
                className={styles.btnGhost}
                size="large"
                block
                icon={<RightOutlined />}
                iconPosition="end"
                onClick={onGoStudents}
              >
                {t("dashboard.viewStudents")}
              </Button>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
